import themes, { type FormTemplateTheme } from '../templates/inputData/_themes';
import type { DesignVariant, DesignVariantId, LayoutStyle } from './types';

/**
 * A single visual flavor inside a vibe slot. Each slot below has multiple
 * options that we rotate through so the suggestions feel fresh instead of
 * always showing the exact same three themes.
 */
interface SlotOption {
  /** Short label shown on the card so the user knows which theme is active. */
  themeLabel: string;
  themeKey: keyof typeof themes;
  theme: FormTemplateTheme;
  preferWelcomeVideo: boolean;
}

interface SlotDefinition {
  id: DesignVariantId;
  title: string;
  vibe: string;
  description: string;
  layoutStyle: LayoutStyle;
  /** Pool of theme options for this slot — rotated by `buildVariants`. */
  options: SlotOption[];
}

const SLOTS: SlotDefinition[] = [
  {
    id: 'boldAndVibrant',
    title: 'Bold & Vibrant',
    vibe: 'more energy and color',
    description:
      'Punchy theme, alternating split layouts, and full-bleed visuals to make every question feel alive.',
    layoutStyle: 'visualSplit',
    options: [
      {
        themeLabel: 'Coral Mint',
        themeKey: 'coralMintBold',
        theme: themes.coralMintBold,
        preferWelcomeVideo: true,
      },
      {
        themeLabel: 'Sunset Gold',
        themeKey: 'sunsetGold',
        theme: themes.sunsetGold,
        preferWelcomeVideo: true,
      },
      {
        themeLabel: 'Lavender Pop',
        themeKey: 'plumLavenderPop',
        theme: themes.plumLavenderPop,
        preferWelcomeVideo: true,
      },
    ],
  },
  {
    id: 'sleekAndModern',
    title: 'Sleek & Modern',
    vibe: 'premium and cinematic',
    description:
      'Moody dark theme with full-bleed imagery behind every question for a high-end, agency-built feel.',
    layoutStyle: 'cinematic',
    options: [
      {
        themeLabel: 'Dark Midnight',
        themeKey: 'darkMidnight',
        theme: themes.darkMidnight,
        preferWelcomeVideo: true,
      },
      {
        themeLabel: 'Midnight Coral',
        themeKey: 'midnightCoral',
        theme: themes.midnightCoral,
        preferWelcomeVideo: true,
      },
      {
        themeLabel: 'Pure Black',
        themeKey: 'blackMidnight',
        theme: themes.blackMidnight,
        preferWelcomeVideo: true,
      },
    ],
  },
  {
    id: 'softAndPremium',
    title: 'Soft & Premium',
    vibe: 'elegant and refined',
    description:
      'Editorial layouts with detached imagery and a warm, sophisticated palette that feels expensive.',
    layoutStyle: 'editorial',
    options: [
      {
        themeLabel: 'Deep Rose',
        themeKey: 'deepRoseIvory',
        theme: themes.deepRoseIvory,
        preferWelcomeVideo: false,
      },
      {
        themeLabel: 'Soft Blue Clay',
        themeKey: 'softBlueClay',
        theme: themes.softBlueClay,
        preferWelcomeVideo: false,
      },
      {
        themeLabel: 'Emerald Honey',
        themeKey: 'emeraldHoneyCream',
        theme: themes.emeraldHoneyCream,
        preferWelcomeVideo: false,
      },
    ],
  },
];

const safeMod = (n: number, len: number) => ((n % len) + len) % len;

/**
 * Build the three variant cards using a rotation offset. Each slot picks its
 * option as `(rotation + slotIndex) % poolSize`, which means consecutive
 * shuffles always change every card (rather than rotating in lockstep where
 * one card might pick the same theme twice in a row).
 */
export function buildVariants(rotation = 0): DesignVariant[] {
  return SLOTS.map((slot, slotIndex) => {
    const option = slot.options[safeMod(rotation + slotIndex, slot.options.length)]!;
    return {
      id: slot.id,
      title: slot.title,
      vibe: slot.vibe,
      description: slot.description,
      layoutStyle: slot.layoutStyle,
      themeKey: option.themeKey,
      theme: option.theme,
      themeLabel: option.themeLabel,
      preferWelcomeVideo: option.preferWelcomeVideo,
    };
  });
}

/** Total number of distinct shuffle states before the rotation repeats. */
export const VARIANT_ROTATION_LENGTH = SLOTS.reduce(
  (acc, s) => acc * s.options.length,
  1,
);

/** Default (rotation = 0) variants — kept exported for any consumers that
 *  want a stable baseline reference. */
export const DESIGN_VARIANTS: Record<DesignVariantId, DesignVariant> = buildVariants(0).reduce(
  (acc, v) => {
    acc[v.id] = v;
    return acc;
  },
  {} as Record<DesignVariantId, DesignVariant>,
);

export const ALL_VARIANTS: DesignVariant[] = buildVariants(0);
